import { Fragment, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ChevronDown, ChevronRight, FileText, XCircle, Download, Eye } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { TableSkeleton } from '@/components/common/TableSkeleton'
import { EmptyState } from '@/components/common/EmptyState'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { usePayrollRunItem } from '@/features/payroll/hooks/usePayrollRuns'
import { useConsolidatedItem } from '@/features/payroll/hooks/usePayrollBatches'
import {
  downloadBatchSlipPdf,
  downloadBatchSlipExcel,
} from '@/api/services/payroll-batches.service'
import {
  downloadPayrollPaymentSlipPdf,
  getPayrollPreviewSlipHtml,
} from '@/api/services/payroll-live.service'
import { triggerBlobDownload, openBlobInNewTab } from '@/utils/download'
import { StatusBadge } from './StatusBadge'
import { StatCard } from './StatCard'
import type {
  PayrollItem,
  PayrollLine,
  PayrollTaxLine,
  ConsolidatedPayrollItem,
  PayrollPreviewEmployee,
  PayrollSelectionPreview,
} from '@/features/payroll/types/payroll.types'

const PAYROLL_FORECAST_STATE_KEY = 'payroll:forecast:workspace'

const SOURCE_VARIANT: Record<string, 'neutral' | 'success' | 'danger' | 'warning' | 'info'> = {
  compensation: 'neutral',
  allowance: 'info',
  bonus: 'success',
  overtime: 'info',
  penalty: 'danger',
  security_ledger: 'warning',
  attendance: 'warning',
  leave_deduction: 'danger',
  reimbursement: 'success',
  tax: 'neutral',
}

function getEmployeeName(emp: PayrollItem['employeeId']): string {
  if (typeof emp === 'string') return emp
  return [emp.firstName, emp.lastName].filter(Boolean).join(' ') || emp.email || emp._id
}

function readForecastPreview(): PayrollSelectionPreview | undefined {
  try {
    const raw = window.sessionStorage.getItem(PAYROLL_FORECAST_STATE_KEY)
    if (!raw) return undefined
    const parsed = JSON.parse(raw) as { preview?: PayrollSelectionPreview }
    return parsed.preview
  } catch {
    return undefined
  }
}

// ─── Company Run Item View ─────────────────────────

function RunItemView({ companyId, runId, itemId }: { companyId: string; runId: string; itemId: string }): React.ReactElement {
  const { data: item, isLoading, isError } = usePayrollRunItem(companyId, runId, itemId)

  if (isLoading) return <TableSkeleton rows={4} cols={3} />
  if (isError || !item) return <EmptyState icon={XCircle} title="Payroll item not found" />

  const period = `${item.payrollMonth.year}-${String(item.payrollMonth.month).padStart(2, '0')}`

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Payroll Runs', to: `/companies/${companyId}/payroll/runs` },
        { label: `Run ${period}`, to: `/companies/${companyId}/payroll/runs/${runId}` },
        { label: getEmployeeName(item.employeeId) },
      ]}
      icon={FileText}
      title={getEmployeeName(item.employeeId)}
      description={`${period} · ${item.currency}`}
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar actions={<StatusBadge status={item.status} />} />
        <ItemContent item={item} />
      </div>
    </PageShell>
  )
}

// ─── Consolidated Item View ────────────────────────

function SlipDownloadButtons({ batchId, employeeId, period }: { batchId: string; employeeId: string; period: string }): React.ReactElement {
  const [busy, setBusy] = useState<'view' | 'pdf' | 'excel' | null>(null)

  const handleView = async (): Promise<void> => {
    setBusy('view')
    try {
      const blob = await downloadBatchSlipPdf(batchId, employeeId)
      openBlobInNewTab(blob)
    } finally {
      setBusy(null)
    }
  }

  const handleDownload = async (format: 'pdf' | 'excel'): Promise<void> => {
    setBusy(format)
    try {
      const blob =
        format === 'pdf'
          ? await downloadBatchSlipPdf(batchId, employeeId)
          : await downloadBatchSlipExcel(batchId, employeeId)
      triggerBlobDownload(blob, `salary-slip-${period}.${format === 'pdf' ? 'pdf' : 'xlsx'}`)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Button variant="secondary" size="sm" disabled={busy !== null} onClick={() => void handleView()}>
        <Eye className="h-4 w-4" />
        {busy === 'view' ? 'Opening…' : 'View'}
      </Button>
      <Button variant="secondary" size="sm" disabled={busy !== null} onClick={() => void handleDownload('pdf')}>
        <Download className="h-4 w-4" />
        {busy === 'pdf' ? 'Preparing…' : 'PDF'}
      </Button>
      <Button variant="secondary" size="sm" disabled={busy !== null} onClick={() => void handleDownload('excel')}>
        <Download className="h-4 w-4" />
        {busy === 'excel' ? 'Preparing…' : 'Excel'}
      </Button>
    </div>
  )
}

function ConsolidatedItemView({ batchId, employeeId }: { batchId: string; employeeId: string }): React.ReactElement {
  const { data: item, isLoading, isError } = useConsolidatedItem(batchId, employeeId)

  if (isLoading) return <TableSkeleton rows={4} cols={3} />
  if (isError || !item) return <EmptyState icon={XCircle} title="Consolidated item not found" />

  const period = `${item.payrollMonth.year}-${String(item.payrollMonth.month).padStart(2, '0')}`
  const employeeName = getEmployeeName(item.employeeId)

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Batches', to: '/payroll' },
        { label: `Batch ${period}`, to: `/payroll/batches/${batchId}` },
        { label: employeeName },
      ]}
      icon={FileText}
      title={employeeName}
      description={`${period} · ${item.currency}`}
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          actions={
            <>
              <SlipDownloadButtons batchId={batchId} employeeId={employeeId} period={period} />
              <StatusBadge status={item.status} />
            </>
          }
        />
        <ConsolidatedContent item={item} />
      </div>
    </PageShell>
  )
}

// ─── Runtime Preview Item View ─────────────────────

function PreviewItemView({
  companyId,
  employeeId,
}: {
  companyId: string
  employeeId: string
}): React.ReactElement {
  const [downloadingHeaderSlip, setDownloadingHeaderSlip] = useState(false)
  const [viewingHeaderSlip, setViewingHeaderSlip] = useState(false)
  const preview = readForecastPreview()
  const rows = preview?.companies
    .flatMap((company) => company.employees)
    .filter(
      (employee) =>
        employee.employee.id === employeeId &&
        (companyId === 'all' || employee.company.id === companyId),
    ) ?? []
  const row = rows[0]

  if (!preview || !row) {
    return (
      <PageShell
        breadcrumbItems={[
          { label: 'Payroll', to: '/payroll' },
          { label: 'Calculate', to: '/payroll/forecast' },
          { label: 'Salary detail' },
        ]}
        icon={FileText}
        title="Salary detail"
        className="gap-0"
        hideHeader
      >
        <EmptyState
          icon={XCircle}
          title="Preview salary detail not found"
          description="Go back to Calculate Payroll and calculate the preview again."
        />
      </PageShell>
    )
  }

  const period = new Date(preview.asOf).toLocaleString(undefined, {
    month: 'long',
    year: 'numeric',
  })
  const employeeName = row.employee.name ?? row.employee.id
  const companyNames = rows.map((employee) => employee.company.name)
  const currencies = new Set(rows.map((employee) => employee.currency).filter(Boolean))
  const paidRow = rows.find((employee) => employee.paidContext)

  const downloadHeaderSlip = async (): Promise<void> => {
    if (!paidRow?.paidContext) return
    setDownloadingHeaderSlip(true)
    try {
      const blob = await downloadPayrollPaymentSlipPdf(
        paidRow.paidContext.snapshotId,
        paidRow.paidContext.itemId,
      )
      triggerBlobDownload(blob, `salary-slip-${paidRow.paidContext.slipNumber ?? paidRow.paidContext.itemId}.pdf`)
    } finally {
      setDownloadingHeaderSlip(false)
    }
  }

  const viewHeaderSlip = async (): Promise<void> => {
    setViewingHeaderSlip(true)
    try {
      if (paidRow?.paidContext) {
        const blob = await downloadPayrollPaymentSlipPdf(
          paidRow.paidContext.snapshotId,
          paidRow.paidContext.itemId,
        )
        openBlobInNewTab(blob)
        return
      }
      const blob = await getPayrollPreviewSlipHtml({
        companyIds: rows.map((employee) => employee.company.id),
        employeeIds: [employeeId],
        asOf: preview.asOf,
      })
      openBlobInNewTab(blob)
    } finally {
      setViewingHeaderSlip(false)
    }
  }

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Payroll', to: '/payroll' },
        { label: 'Calculate', to: '/payroll/forecast' },
        { label: employeeName },
      ]}
      icon={FileText}
      title={employeeName}
      description={`${period} · ${companyNames.join(', ')} · ${
        currencies.size === 1 ? [...currencies][0] : 'Mixed currency'
      }`}
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          actions={
            <>
              <SlipStatusBadge paid={Boolean(paidRow?.paidContext)} />
              <Button
                variant="secondary"
                size="sm"
                disabled={viewingHeaderSlip}
                onClick={() => void viewHeaderSlip()}
              >
                <Eye className="h-3.5 w-3.5" />
                <span className="ml-1.5">{viewingHeaderSlip ? 'Opening...' : 'View slip'}</span>
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!paidRow?.paidContext || downloadingHeaderSlip}
                title={paidRow?.paidContext ? 'Download paid salary slip' : 'Pay this payroll before downloading the final slip'}
                onClick={() => void downloadHeaderSlip()}
              >
                <Download className="h-3.5 w-3.5" />
                <span className="ml-1.5">
                  {paidRow?.paidContext
                    ? downloadingHeaderSlip
                      ? 'Preparing...'
                      : 'Download slip'
                    : 'Download after pay'}
                </span>
              </Button>
              <PreviewStatusBadge rows={rows} />
            </>
          }
        />
        <PreviewItemContent rows={rows} asOf={preview.asOf} />
      </div>
    </PageShell>
  )
}

function PreviewStatusBadge({ rows }: { rows: PayrollPreviewEmployee[] }): React.ReactElement {
  if (rows.some((row) => row.status === 'ready')) return <Badge variant="success">Ready</Badge>
  if (rows.every((row) => row.status === 'paid')) return <Badge variant="info">Paid</Badge>
  return <Badge variant="warning">Pending</Badge>
}

function SlipStatusBadge({ paid }: { paid: boolean }): React.ReactElement {
  if (paid) return <Badge variant="success">Paid final slip</Badge>
  return <Badge variant="warning">Unpaid preview</Badge>
}

function formatPreviewMoney(value: number | undefined, currency?: string): string {
  if (typeof value !== 'number') return '—'
  return `${currency ?? ''} ${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`.trim()
}

function formatShortDate(value: string | undefined): string {
  if (!value) return '—'
  return new Date(value).toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function formatPayrollMonth(period: { year: number; month: number } | undefined): string {
  if (!period) return '—'
  return new Date(Date.UTC(period.year, period.month - 1, 1)).toLocaleString(undefined, {
    month: 'long',
    year: 'numeric',
  })
}

function countWeekdaysInMonth(year: number, month: number): number {
  const cursor = new Date(Date.UTC(year, month - 1, 1))
  let count = 0
  while (cursor.getUTCMonth() === month - 1) {
    const day = cursor.getUTCDay()
    if (day !== 0 && day !== 6) count += 1
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }
  return count
}

function getMonthWindow(period: { year: number; month: number }): { start: string; end: string } {
  return {
    start: new Date(Date.UTC(period.year, period.month - 1, 1)).toISOString(),
    end: new Date(Date.UTC(period.year, period.month, 0, 23, 59, 59, 999)).toISOString(),
  }
}

function getCompensationLabel(employee: PayrollPreviewEmployee): {
  basis: string
  amount: string
  source: string
} {
  const compensation = employee.compensation
  const type = compensation?.type ?? employee.compensationType
  if (!compensation || !type) {
    const totalHours = employee.regularHours + employee.overtimeHours
    if (totalHours > 0 && employee.gross > 0) {
      return {
        basis: 'Effective hourly',
        amount: `${formatPreviewMoney(employee.gross / totalHours, employee.currency)} / hr`,
        source: 'Derived from preview',
      }
    }
    if (employee.gross > 0) {
      return {
        basis: 'Effective salary',
        amount: `${formatPreviewMoney(employee.gross, employee.currency)} / period`,
        source: 'Derived from preview',
      }
    }
    return { basis: 'Not configured', amount: '—', source: 'Recalculate preview' }
  }

  const source =
    compensation.source === 'company_override' ? 'Company override' : 'Employee profile'

  if (type === 'hourly') {
    return {
      basis: 'Hourly',
      amount: `${formatPreviewMoney(compensation.hourlyRate, compensation.currency)} / hr`,
      source,
    }
  }

  return {
    basis: 'Salaried',
    amount: `${formatPreviewMoney(compensation.baseSalary, compensation.currency)} / month`,
    source,
  }
}

function getPreviewFormula(employee: PayrollPreviewEmployee): string {
  const compensation = getCompensationLabel(employee)
  const totalHours = employee.regularHours + employee.overtimeHours
  const parts =
    compensation.basis === 'Hourly' || compensation.basis === 'Effective hourly'
      ? [`${totalHours.toLocaleString()} hrs x ${compensation.amount.replace(' / hr', '')}`]
      : [compensation.amount]

  if (employee.deductions > 0) parts.push(`- ${formatPreviewMoney(employee.deductions, employee.currency)} deductions`)
  if (employee.tax > 0) parts.push(`- ${formatPreviewMoney(employee.tax, employee.currency)} tax`)
  parts.push(`= ${formatPreviewMoney(employee.netPay, employee.currency)} net`)
  return parts.join(' ')
}

function getPreviewLines(employee: PayrollPreviewEmployee): PayrollLine[] {
  if (employee.lines && employee.lines.length > 0) return employee.lines
  const lines: PayrollLine[] = []
  if (employee.gross > 0) {
    lines.push({
      sourceType: 'compensation',
      description: 'Gross earnings',
      amount: employee.gross,
      currency: employee.currency ?? '',
    })
  }
  if (employee.deductions > 0) {
    lines.push({
      sourceType: 'penalty',
      description: 'Deductions',
      amount: -employee.deductions,
      currency: employee.currency ?? '',
    })
  }
  return lines
}

function getCoverageDisplay(
  employee: PayrollPreviewEmployee,
  asOf: string,
): {
  payrollMonth: string
  payWindow: string
  paidDays: string
  prorate: string
  reason: string
} {
  if (employee.periodCoverage) {
    return {
      payrollMonth: formatPayrollMonth(employee.periodCoverage.payrollMonth),
      payWindow: `${formatShortDate(employee.periodCoverage.payPeriodStart)} - ${formatShortDate(employee.periodCoverage.payPeriodEnd)}`,
      paidDays: `${employee.periodCoverage.paidDays} / ${employee.periodCoverage.workingDays}`,
      prorate: `${(employee.periodCoverage.proRateFactor * 100).toFixed(1)}%`,
      reason: employee.periodCoverage.proRateReason ?? 'Full period',
    }
  }

  const asOfDate = new Date(asOf)
  const payrollMonth = {
    year: asOfDate.getUTCFullYear(),
    month: asOfDate.getUTCMonth() + 1,
  }
  const monthWindow = getMonthWindow(payrollMonth)
  const workingDays = countWeekdaysInMonth(payrollMonth.year, payrollMonth.month)
  const compensation = getCompensationLabel(employee)
  const isHoursBased =
    compensation.basis === 'Hourly' || compensation.basis === 'Effective hourly'

  return {
    payrollMonth: formatPayrollMonth(payrollMonth),
    payWindow: `${formatShortDate(monthWindow.start)} - ${formatShortDate(monthWindow.end)}`,
    paidDays: isHoursBased ? 'Hours based' : `${workingDays} / ${workingDays}`,
    prorate: isHoursBased ? 'N/A' : '100.0%',
    reason: isHoursBased ? 'Hourly calculation' : 'Derived full period',
  }
}

function PreviewCompanyBreakdown({
  rows,
  asOf,
}: {
  rows: PayrollPreviewEmployee[]
  asOf: string
}): React.ReactElement {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(rows.length === 1 ? [rows[0].company.id] : []))
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null)

  const toggleExpanded = (companyId: string): void => {
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(companyId)) next.delete(companyId)
      else next.add(companyId)
      return next
    })
  }

  const downloadPaidSlip = async (employee: PayrollPreviewEmployee): Promise<void> => {
    if (!employee.paidContext) return
    const key = `${employee.paidContext.snapshotId}:${employee.paidContext.itemId}`
    setDownloadingKey(key)
    try {
      const blob = await downloadPayrollPaymentSlipPdf(
        employee.paidContext.snapshotId,
        employee.paidContext.itemId,
      )
      triggerBlobDownload(blob, `salary-slip-${employee.paidContext.slipNumber ?? employee.paidContext.itemId}.pdf`)
    } finally {
      setDownloadingKey(null)
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="border-b border-gray-100 px-4 py-3 text-sm font-medium text-gray-900 dark:border-gray-700 dark:text-gray-100">
        Company breakdown
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
              <th className="px-4 py-3">Company</th>
              <th className="px-4 py-3">Pay basis</th>
              <th className="px-4 py-3 text-right">Rate / salary</th>
              <th className="px-4 py-3 text-right">Hours</th>
              <th className="px-4 py-3 text-right">OT</th>
              <th className="px-4 py-3 text-right">Gross</th>
              <th className="px-4 py-3 text-right">Tax</th>
              <th className="px-4 py-3 text-right">Net</th>
              <th className="px-4 py-3 text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((employee) => {
              const compensation = getCompensationLabel(employee)
              const coverage = getCoverageDisplay(employee, asOf)
              const isExpanded = expanded.has(employee.company.id)
              return (
                <Fragment key={employee.company.id}>
                  <tr
                    className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/50"
                    onClick={() => toggleExpanded(employee.company.id)}
                  >
                    <td className="px-4 py-3 font-medium">
                      <span className="flex items-center gap-2">
                        {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                        {employee.company.name}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 dark:text-gray-100">{compensation.basis}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{compensation.source}</div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{compensation.amount}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{employee.regularHours}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{employee.overtimeHours}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatPreviewMoney(employee.gross, employee.currency)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatPreviewMoney(employee.tax, employee.currency)}</td>
                    <td className="px-4 py-3 text-right font-medium tabular-nums">{formatPreviewMoney(employee.netPay, employee.currency)}</td>
                    <td className="px-4 py-3 text-right">
                      <Badge variant={employee.status === 'ready' ? 'success' : employee.status === 'paid' ? 'info' : 'warning'}>
                        {employee.status === 'blocked' ? 'Pending' : employee.status}
                      </Badge>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="border-b border-gray-100 bg-gray-50/70 dark:border-gray-700 dark:bg-gray-800/30">
                      <td colSpan={9} className="px-4 py-4">
                        <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
                          <div className="rounded-md border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
                            <div className="mb-3 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Pay components</div>
                            <LinesTable lines={getPreviewLines(employee)} />
                          </div>
                          <div className="space-y-4">
                            <div className="rounded-md border border-gray-200 bg-white p-4 text-sm dark:border-gray-700 dark:bg-gray-900">
                              <div className="mb-3 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Period coverage</div>
                              <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                                <dt className="text-gray-500 dark:text-gray-400">Payroll month</dt>
                                <dd className="text-right font-medium text-gray-900 dark:text-gray-100">{coverage.payrollMonth}</dd>
                                <dt className="text-gray-500 dark:text-gray-400">Pay window</dt>
                                <dd className="text-right font-medium text-gray-900 dark:text-gray-100">
                                  {coverage.payWindow}
                                </dd>
                                <dt className="text-gray-500 dark:text-gray-400">Paid days</dt>
                                <dd className="text-right font-medium text-gray-900 dark:text-gray-100">
                                  {coverage.paidDays}
                                </dd>
                                <dt className="text-gray-500 dark:text-gray-400">Prorate</dt>
                                <dd className="text-right font-medium text-gray-900 dark:text-gray-100">
                                  {coverage.prorate}
                                </dd>
                                <dt className="text-gray-500 dark:text-gray-400">Reason</dt>
                                <dd className="text-right font-medium text-gray-900 dark:text-gray-100">
                                  {coverage.reason}
                                </dd>
                              </dl>
                            </div>
                            <div className="rounded-md border border-gray-200 bg-white p-4 text-sm dark:border-gray-700 dark:bg-gray-900">
                              <div className="mb-2 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Formula</div>
                              <div className="text-gray-700 dark:text-gray-300">{getPreviewFormula(employee)}</div>
                            </div>
                            {employee.paidContext && (
                              <div className="rounded-md border border-green-200 bg-green-50 p-4 text-sm dark:border-green-900/60 dark:bg-green-900/10">
                                <div className="mb-3 text-xs font-medium uppercase text-green-700 dark:text-green-300">Paid context</div>
                                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-gray-700 dark:text-gray-300">
                                  <dt>Paid at</dt>
                                  <dd className="text-right font-medium">{new Date(employee.paidContext.paidAt).toLocaleString()}</dd>
                                  <dt>Slip number</dt>
                                  <dd className="text-right font-medium">{employee.paidContext.slipNumber ?? '—'}</dd>
                                  <dt>Paid by</dt>
                                  <dd className="text-right font-medium">{employee.paidContext.paidBy}</dd>
                                </dl>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  className="mt-4 w-full justify-center"
                                  disabled={downloadingKey !== null}
                                  onClick={() => void downloadPaidSlip(employee)}
                                >
                                  <Download className="mr-1.5 h-3.5 w-3.5" />
                                  {downloadingKey === `${employee.paidContext.snapshotId}:${employee.paidContext.itemId}` ? 'Preparing...' : 'Download slip'}
                                </Button>
                              </div>
                            )}
                            {employee.taxLines && employee.taxLines.length > 0 && (
                              <div className="rounded-md border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
                                <TaxTable taxLines={employee.taxLines} />
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function PreviewItemContent({
  rows,
  asOf,
}: {
  rows: PayrollPreviewEmployee[]
  asOf: string
}): React.ReactElement {
  const row = rows[0]
  const totals = rows.reduce(
    (acc, employee) => ({
      regularHours: acc.regularHours + employee.regularHours,
      overtimeHours: acc.overtimeHours + employee.overtimeHours,
      gross: acc.gross + employee.gross,
      deductions: acc.deductions + employee.deductions,
      tax: acc.tax + employee.tax,
      netPay: acc.netPay + employee.netPay,
    }),
    {
      regularHours: 0,
      overtimeHours: 0,
      gross: 0,
      deductions: 0,
      tax: 0,
      netPay: 0,
    },
  )
  const currencies = new Set(rows.map((employee) => employee.currency).filter(Boolean))
  const currency = currencies.size === 1 ? [...currencies][0] : undefined

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/10 dark:text-amber-300">
        Runtime preview calculated {new Date(asOf).toLocaleString()}. Final slips use the saved paid snapshot.
      </div>

      {rows.some((employee) => employee.warning) && (
        <div className="rounded-lg border border-amber-200 px-4 py-3 text-sm text-amber-700 dark:border-amber-900/50 dark:text-amber-300">
          {rows.map((employee) => employee.warning).filter(Boolean).join(', ')}
        </div>
      )}

      <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <p className="mb-2 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
          Employee
        </p>
        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <span>Name: <strong>{row.employee.name ?? row.employee.id}</strong></span>
          <span>Companies: <strong>{rows.map((employee) => employee.company.name).join(', ')}</strong></span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Regular Hours" value={totals.regularHours} />
        <StatCard label="Overtime Hours" value={totals.overtimeHours} />
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Gross" value={totals.gross} currency={currency} />
        <StatCard label="Deductions" value={totals.deductions} currency={currency} />
        <StatCard label="Tax" value={totals.tax} currency={currency} />
        <StatCard label="Net Pay" value={totals.netPay} currency={currency} />
      </div>

      <PreviewCompanyBreakdown rows={rows} asOf={asOf} />
    </div>
  )
}

// ─── Shared PayrollItem Content ────────────────────

function ItemContent({ item }: { item: PayrollItem }): React.ReactElement {
  const multiCurrency = item.originalCurrency && item.originalCurrency !== item.currency

  return (
    <div className="space-y-6">
      {/* Compensation snapshot */}
      <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <p className="mb-2 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Compensation Snapshot</p>
        <div className="flex flex-wrap gap-4 text-sm">
          {item.compensationSnapshot.baseSalary != null && (
            <span>Base: <strong>{String(item.compensationSnapshot.baseSalary)}</strong></span>
          )}
          {item.compensationSnapshot.currency != null && (
            <span>Currency: {String(item.compensationSnapshot.currency)}</span>
          )}
          {item.compensationSnapshot.source != null && (
            <span>Source: {String(item.compensationSnapshot.source)}</span>
          )}
        </div>
      </div>

      {/* Pro-rate */}
      {item.proRateFactor < 1 && (
        <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700 dark:border-blue-900/50 dark:bg-blue-900/20 dark:text-blue-300">
          Pro-rated: {(item.proRateFactor * 100).toFixed(1)}%
          {item.proRateReason && <span> — {item.proRateReason}</span>}
        </div>
      )}

      {/* Hours */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Regular Hours" value={item.regularHours} />
        <StatCard label="Overtime Hours" value={item.overtimeHours} />
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Gross" value={item.totals?.gross} currency={item.currency} />
        <StatCard label="Net Pay" value={item.totals?.netPay} currency={item.currency} />
        <StatCard label="Total Tax" value={item.totals?.totalTax} currency={item.currency} />
        <StatCard label="Total Deductions" value={item.totals?.totalDeductions} currency={item.currency} />
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Bonuses" value={item.totals?.totalBonuses} currency={item.currency} />
        <StatCard label="Penalties" value={item.totals?.totalPenalties} currency={item.currency} />
        <StatCard label="Security Ded." value={item.totals?.totalSecurityDeductions} currency={item.currency} />
        <StatCard label="Overtime Pay" value={item.totals?.totalOvertimePay} currency={item.currency} />
      </div>

      {/* Currency conversion */}
      {multiCurrency && item.exchangeRate && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-gray-700 dark:bg-gray-800">
          <span className="font-medium">Currency Conversion: </span>
          {item.originalAmount?.toLocaleString()} {item.originalCurrency} →{' '}
          {item.convertedAmount?.toLocaleString()} {item.currency} @ {item.exchangeRate.rate}
        </div>
      )}

      {/* Line items */}
      <LinesTable lines={item.lines} />

      {/* Tax lines */}
      <TaxTable taxLines={item.taxLines} />
    </div>
  )
}

// ─── Shared ConsolidatedItem Content ───────────────

function ConsolidatedContent({ item }: { item: ConsolidatedPayrollItem }): React.ReactElement {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Combined Gross" value={item.totals.combinedGross} currency={item.currency} />
        <StatCard label="Combined Tax" value={item.totals.combinedTax} currency={item.currency} />
        <StatCard label="Combined Net" value={item.totals.combinedNet} currency={item.currency} />
        <StatCard label="Bonuses" value={item.totals.combinedBonuses} currency={item.currency} />
      </div>

      {/* Company breakdown */}
      {item.companyBreakdown.map((cb, i) => (
        <div key={i} className="rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="border-b border-gray-200 px-4 py-3 text-sm font-medium dark:border-gray-700">
            {cb.companyName ?? `Company ${cb.companyId}`}
            <span className="ml-2 text-gray-500">
              Gross: {cb.gross.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="px-4 py-3">
            <LinesTable lines={cb.lines} />
          </div>
        </div>
      ))}

      <TaxTable taxLines={item.taxLines} />
    </div>
  )
}

// ─── Reusable sub-tables ───────────────────────────

function LinesTable({ lines }: { lines: PayrollLine[] }): React.ReactElement | null {
  if (lines.length === 0) return null
  return (
    <div>
      <h3 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Line Items</h3>
      <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700">
            <th className="py-2 pr-4">Type</th>
            <th className="py-2 pr-4">Description</th>
            <th className="py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, i) => (
            <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
              <td className="py-2 pr-4">
                <Badge variant={SOURCE_VARIANT[line.sourceType] ?? 'neutral'}>
                  {line.sourceType.replace('_', ' ')}
                </Badge>
              </td>
              <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{line.description}</td>
              <td className={`py-2 text-right tabular-nums font-medium ${line.amount < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                {line.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TaxTable({ taxLines }: { taxLines: PayrollTaxLine[] }): React.ReactElement | null {
  if (taxLines.length === 0) return null
  return (
    <div>
      <h3 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Tax Breakdown</h3>
      <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700">
            <th className="px-4 py-2">Rule</th>
            <th className="px-4 py-2 text-right">Taxable</th>
            <th className="px-4 py-2 text-right">Tax</th>
            <th className="px-4 py-2 text-right">Rate</th>
          </tr>
        </thead>
        <tbody>
          {taxLines.map((tl, i) => (
            <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
              <td className="px-4 py-2">
                {tl.ruleName}
                {tl.isEstimate && <Badge variant="warning" className="ml-2">Estimate</Badge>}
              </td>
              <td className="px-4 py-2 text-right tabular-nums">{tl.taxableAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
              <td className="px-4 py-2 text-right tabular-nums font-medium">{tl.taxAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
              <td className="px-4 py-2 text-right tabular-nums">{(tl.rate * 100).toFixed(2)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── Router entry ──────────────────────────────────

export function RunItemDetailPage(): React.ReactElement {
  const { companyId, runId, itemId, batchId, employeeId } = useParams<{
    companyId?: string
    runId?: string
    itemId?: string
    batchId?: string
    employeeId?: string
  }>()

  // Batch consolidated context
  if (batchId && employeeId) {
    return <ConsolidatedItemView batchId={batchId} employeeId={employeeId} />
  }

  // Company run item context
  if (companyId && runId && itemId) {
    return <RunItemView companyId={companyId} runId={runId} itemId={itemId} />
  }

  // Runtime calculate-preview context
  if (companyId && employeeId) {
    return <PreviewItemView companyId={companyId} employeeId={employeeId} />
  }

  return (
    <PageShell breadcrumbItems={[{ label: 'Payroll' }]} icon={FileText} title="Item" className="gap-0" hideHeader>
      <EmptyState icon={XCircle} title="Invalid route" />
    </PageShell>
  )
}

RunItemDetailPage.displayName = 'RunItemDetailPage'
